"use client"

import { useState, useEffect } from "react"
import {
  Filter,
  Download,
  DollarSign,
  TrendingUp,
  Users,
  Calendar,
  Search,
  X,
  ChevronUp,
  ChevronDown,
  Eye,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"

interface PayReport {
  id: string
  agentId: string
  agentName: string
  commissionAmount: number
  overrideAmount: number
  totalAmount: number
  status: "PENDING" | "PAID" | "UNPAID"
  payPeriodStart: string
  payPeriodEnd: string
  payMethod: string
  createdAt: string
  organizationId: string
  organizationName: string
  organizationType: "EM" | "DM" | "CM" | "BM" | "AM" | "M"
  managerId: string
  managerName: string
}

type SortField =
  | "agentName"
  | "organizationName"
  | "managerName"
  | "commissionAmount"
  | "overrideAmount"
  | "totalAmount"
  | "status"
  | "payPeriodStart"
  | "createdAt"
type SortDirection = "asc" | "desc"

export default function FMOGlobalPayReports() {
  const [payReports, setPayReports] = useState<PayReport[]>([])
  const [filteredReports, setFilteredReports] = useState<PayReport[]>([])
  const [loading, setLoading] = useState(true)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Sorting state
  const [sortField, setSortField] = useState<SortField>("organizationName")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")

  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: "",
    status: "all",
    organizationType: "all",
    payPeriod: "current-week",
  })

  // Mock data for global pay reports
  useEffect(() => {
    const mockPayReports: PayReport[] = [
      // Eastern Region EM
      {
        id: "pr1",
        agentId: "a1",
        agentName: "John Smith",
        commissionAmount: 1250,
        overrideAmount: 312.5,
        totalAmount: 1562.5,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "em1",
        organizationName: "Eastern Region EM",
        organizationType: "EM",
        managerId: "m1",
        managerName: "Sarah Johnson",
      },
      {
        id: "pr2",
        agentId: "a2",
        agentName: "Mike Davis",
        commissionAmount: 1600,
        overrideAmount: 400,
        totalAmount: 2000,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "dm1",
        organizationName: "Eastern DM Alpha",
        organizationType: "DM",
        managerId: "m2",
        managerName: "Tom Wilson",
      },
      {
        id: "pr3",
        agentId: "a3",
        agentName: "Lisa Brown",
        commissionAmount: 900,
        overrideAmount: 225,
        totalAmount: 1125,
        status: "PENDING",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Check",
        createdAt: "2025-01-11",
        organizationId: "dm1",
        organizationName: "Eastern DM Alpha",
        organizationType: "DM",
        managerId: "m2",
        managerName: "Tom Wilson",
      },
      {
        id: "pr4",
        agentId: "a4",
        agentName: "David Lee",
        commissionAmount: 2050,
        overrideAmount: 512.5,
        totalAmount: 2562.5,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "dm2",
        organizationName: "Eastern DM Beta",
        organizationType: "DM",
        managerId: "m3",
        managerName: "Jennifer Clark",
      },
      {
        id: "pr5",
        agentId: "a5",
        agentName: "Robert Johnson",
        commissionAmount: 1350,
        overrideAmount: 337.5,
        totalAmount: 1687.5,
        status: "UNPAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Check",
        createdAt: "2025-01-11",
        organizationId: "dm2",
        organizationName: "Eastern DM Beta",
        organizationType: "DM",
        managerId: "m3",
        managerName: "Jennifer Clark",
      },

      // Western Region EM
      {
        id: "pr6",
        agentId: "a6",
        agentName: "Amanda White",
        commissionAmount: 1450,
        overrideAmount: 362.5,
        totalAmount: 1812.5,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "em2",
        organizationName: "Western Region EM",
        organizationType: "EM",
        managerId: "m4",
        managerName: "Robert Martinez",
      },
      {
        id: "pr7",
        agentId: "a7",
        agentName: "Chris Taylor",
        commissionAmount: 1750,
        overrideAmount: 437.5,
        totalAmount: 2187.5,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "dm3",
        organizationName: "Western DM Gamma",
        organizationType: "DM",
        managerId: "m5",
        managerName: "Maria Rodriguez",
      },
      {
        id: "pr8",
        agentId: "a8",
        agentName: "Rachel Green",
        commissionAmount: 1100,
        overrideAmount: 275,
        totalAmount: 1375,
        status: "UNPAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Check",
        createdAt: "2025-01-11",
        organizationId: "dm3",
        organizationName: "Western DM Gamma",
        organizationType: "DM",
        managerId: "m5",
        managerName: "Maria Rodriguez",
      },
      {
        id: "pr9",
        agentId: "a9",
        agentName: "Kevin Johnson",
        commissionAmount: 1400,
        overrideAmount: 350,
        totalAmount: 1750,
        status: "PENDING",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "dm4",
        organizationName: "Western DM Delta",
        organizationType: "DM",
        managerId: "m6",
        managerName: "James Anderson",
      },
      {
        id: "pr10",
        agentId: "a10",
        agentName: "Michelle Davis",
        commissionAmount: 1850,
        overrideAmount: 462.5,
        totalAmount: 2312.5,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "dm4",
        organizationName: "Western DM Delta",
        organizationType: "DM",
        managerId: "m6",
        managerName: "James Anderson",
      },

      // Central Region EM
      {
        id: "pr11",
        agentId: "a11",
        agentName: "Nicole Adams",
        commissionAmount: 950,
        overrideAmount: 237.5,
        totalAmount: 1187.5,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "em3",
        organizationName: "Central Region EM",
        organizationType: "EM",
        managerId: "m7",
        managerName: "Patricia Thompson",
      },
      {
        id: "pr12",
        agentId: "a12",
        agentName: "Steven Miller",
        commissionAmount: 1650,
        overrideAmount: 412.5,
        totalAmount: 2062.5,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "dm5",
        organizationName: "Central DM Epsilon",
        organizationType: "DM",
        managerId: "m8",
        managerName: "Michael Garcia",
      },
      {
        id: "pr13",
        agentId: "a13",
        agentName: "Jessica Wilson",
        commissionAmount: 1300,
        overrideAmount: 325,
        totalAmount: 1625,
        status: "PENDING",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Check",
        createdAt: "2025-01-11",
        organizationId: "dm5",
        organizationName: "Central DM Epsilon",
        organizationType: "DM",
        managerId: "m8",
        managerName: "Michael Garcia",
      },
      {
        id: "pr14",
        agentId: "a14",
        agentName: "Brian Moore",
        commissionAmount: 2100,
        overrideAmount: 525,
        totalAmount: 2625,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "dm6",
        organizationName: "Central DM Zeta",
        organizationType: "DM",
        managerId: "m9",
        managerName: "Linda Martinez",
      },
      {
        id: "pr15",
        agentId: "a15",
        agentName: "Ashley Thompson",
        commissionAmount: 1200,
        overrideAmount: 300,
        totalAmount: 1500,
        status: "UNPAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Check",
        createdAt: "2025-01-11",
        organizationId: "dm6",
        organizationName: "Central DM Zeta",
        organizationType: "DM",
        managerId: "m9",
        managerName: "Linda Martinez",
      },

      // Additional CM level reports
      {
        id: "pr16",
        agentId: "a16",
        agentName: "Daniel Rodriguez",
        commissionAmount: 800,
        overrideAmount: 200,
        totalAmount: 1000,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "cm1",
        organizationName: "Eastern CM Unit A",
        organizationType: "CM",
        managerId: "m10",
        managerName: "Carlos Mendez",
      },
      {
        id: "pr17",
        agentId: "a17",
        agentName: "Samantha Lee",
        commissionAmount: 1050,
        overrideAmount: 262.5,
        totalAmount: 1312.5,
        status: "PENDING",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "cm2",
        organizationName: "Western CM Unit B",
        organizationType: "CM",
        managerId: "m11",
        managerName: "Diana Foster",
      },
      {
        id: "pr18",
        agentId: "a18",
        agentName: "Marcus Johnson",
        commissionAmount: 1500,
        overrideAmount: 375,
        totalAmount: 1875,
        status: "PAID",
        payPeriodStart: "2025-01-04",
        payPeriodEnd: "2025-01-10",
        payMethod: "Direct Deposit",
        createdAt: "2025-01-11",
        organizationId: "cm3",
        organizationName: "Central CM Unit C",
        organizationType: "CM",
        managerId: "m12",
        managerName: "Anthony Wright",
      },
    ]

    setPayReports(mockPayReports)
    setLoading(false)
  }, [])

  // Apply filters and search
  useEffect(() => {
    let filtered = [...payReports]

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      filtered = filtered.filter(
        (report) =>
          report.agentName.toLowerCase().includes(searchLower) ||
          report.organizationName.toLowerCase().includes(searchLower) ||
          report.managerName.toLowerCase().includes(searchLower) ||
          report.payMethod.toLowerCase().includes(searchLower),
      )
    }

    // Apply status filter
    if (filters.status !== "all") {
      filtered = filtered.filter((report) => report.status === filters.status)
    }

    // Apply organization type filter
    if (filters.organizationType !== "all") {
      filtered = filtered.filter((report) => report.organizationType === filters.organizationType)
    }

    setFilteredReports(filtered)
    setCurrentPage(1) // Reset to first page when filters change
  }, [payReports, filters])

  // Sort reports
  const sortedReports = [...filteredReports].sort((a, b) => {
    let aValue: any = a[sortField]
    let bValue: any = b[sortField]

    // Handle numeric fields
    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue
    }

    // Handle string fields
    if (typeof aValue === "string" && typeof bValue === "string") {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    }

    // Handle date fields
    if (sortField === "payPeriodStart" || sortField === "createdAt") {
      aValue = new Date(aValue).getTime()
      bValue = new Date(bValue).getTime()
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
    return 0
  })

  // Pagination calculations
  const totalItems = sortedReports.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentReports = sortedReports.slice(startIndex, endIndex)

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters({
      searchTerm: "",
      status: "all",
      organizationType: "all",
      payPeriod: "current-week",
    })
  }

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      PAID: "bg-green-100 text-green-800",
      PENDING: "bg-yellow-100 text-yellow-800",
      UNPAID: "bg-red-100 text-red-800",
    }
    return statusStyles[status as keyof typeof statusStyles] || "bg-gray-100 text-gray-800"
  }

  const getOrgTypeBadge = (type: string) => {
    const typeStyles = {
      EM: "bg-blue-100 text-blue-800",
      DM: "bg-green-100 text-green-800",
      CM: "bg-purple-100 text-purple-800",
    }
    return typeStyles[type as keyof typeof typeStyles] || "bg-gray-100 text-gray-800"
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const formatDateRange = (start: string, end: string) => {
    return `${formatDate(start)} - ${formatDate(end)}`
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null
    return sortDirection === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
  }

  const getTotalStats = () => {
    const totalCommissions = filteredReports.reduce((sum, report) => sum + report.commissionAmount, 0)
    const totalOverrides = filteredReports.reduce((sum, report) => sum + report.overrideAmount, 0)
    const totalPayouts = filteredReports.reduce((sum, report) => sum + report.totalAmount, 0)
    const paidCount = filteredReports.filter((report) => report.status === "PAID").length
    const pendingCount = filteredReports.filter((report) => report.status === "PENDING").length

    return { totalCommissions, totalOverrides, totalPayouts, paidCount, pendingCount }
  }

  const stats = getTotalStats()

  // Pagination component
  const renderPagination = () => {
    const pageNumbers = []
    const maxVisiblePages = 5

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i)
    }

    return (
      <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center text-sm text-gray-700">
          <span>
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} results
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-700">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
                setCurrentPage(1)
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {startPage > 1 && (
              <>
                <button
                  onClick={() => setCurrentPage(1)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  1
                </button>
                {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
              </>
            )}

            {pageNumbers.map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  currentPage === page ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            ))}

            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <span className="px-2 text-gray-500">...</span>}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Global Downline Pay Reports</h2>
        <div className="flex gap-2">
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Global Pay Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalCommissions)}</p>
              <p className="text-sm text-gray-600">Total Commissions</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalOverrides)}</p>
              <p className="text-sm text-gray-600">Total Overrides</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalPayouts)}</p>
              <p className="text-sm text-gray-600">Total Payouts</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Calendar className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.paidCount}</p>
              <p className="text-sm text-gray-600">Paid Reports</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Calendar className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.pendingCount}</p>
              <p className="text-sm text-gray-600">Pending Reports</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pay Period Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <div>
            <p className="font-medium text-blue-900">Current Pay Period: Saturday, Jan 4 - Friday, Jan 10, 2025</p>
            <p className="text-sm text-blue-700 mt-1">
              Pay periods run Saturday to Friday. Commission calculations and override distributions are processed
              weekly.
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search agents, organizations, managers..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="PAID">Paid</option>
            <option value="PENDING">Pending</option>
            <option value="UNPAID">Unpaid</option>
          </select>

          <select
            value={filters.organizationType}
            onChange={(e) => handleFilterChange("organizationType", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Organization Types</option>
            <option value="EM">EM Level</option>
            <option value="DM">DM Level</option>
            <option value="CM">CM Level</option>
          </select>

          <button
            onClick={clearFilters}
            className="flex items-center justify-center px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <X className="w-4 h-4 mr-2" />
            Clear Filters
          </button>
        </div>
      </div>

      {/* Pay Reports Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Global Downline Pay Reports ({totalItems} reports)</h3>
          <p className="text-sm text-gray-600 mt-1">
            All pay reports sorted by organizational hierarchy (EM → DM → CM levels)
          </p>
        </div>

        {currentReports.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pay reports found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.searchTerm || filters.status !== "all" || filters.organizationType !== "all"
                ? "Try adjusting your search criteria or filters."
                : "No pay reports available for the current period."}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("agentName")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Agent</span>
                        {getSortIcon("agentName")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("organizationName")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Organization</span>
                        {getSortIcon("organizationName")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("managerName")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Manager</span>
                        {getSortIcon("managerName")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("commissionAmount")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Commission</span>
                        {getSortIcon("commissionAmount")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("overrideAmount")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Override</span>
                        {getSortIcon("overrideAmount")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("totalAmount")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Total Amount</span>
                        {getSortIcon("totalAmount")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {getSortIcon("status")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("payPeriodStart")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Pay Period</span>
                        {getSortIcon("payPeriodStart")}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pay Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{report.agentName}</div>
                          <div className="text-sm text-gray-500">ID: {report.agentId}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getOrgTypeBadge(report.organizationType)}`}
                          >
                            {report.organizationType}
                          </span>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{report.organizationName}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{report.managerName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(report.commissionAmount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(report.overrideAmount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">{formatCurrency(report.totalAmount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(report.status)}`}>
                          {report.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateRange(report.payPeriodStart, report.payPeriodEnd)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{report.payMethod}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 hover:text-blue-900">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="text-green-600 hover:text-green-900">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="text-red-600 hover:text-red-900">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {renderPagination()}
          </>
        )}
      </div>
    </div>
  )
}
